import { useEffect, useState } from 'react'
import { useParams, useSearchParams, Link } from 'react-router-dom'
import { ArrowLeft, List, Settings } from 'lucide-react'
import { anilistService } from '@/services/anilist'
import { Anime } from '@/types/anime'
import { VideoPlayer } from '@/components/VideoPlayer'
import { LoadingSpinner } from '@/components/LoadingSpinner'

interface Episode {
  id: string
  number: number
  title: string
}

export default function WatchPage() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const [anime, setAnime] = useState<Anime | null>(null)
  const [episodes, setEpisodes] = useState<Episode[]>([])
  const [loading, setLoading] = useState(true)
  const [currentEpisode, setCurrentEpisode] = useState(1)
  const [showEpisodeList, setShowEpisodeList] = useState(false)

  useEffect(() => {
    const episodeParam = searchParams.get('episode')
    if (episodeParam) {
      setCurrentEpisode(parseInt(episodeParam))
    }
  }, [searchParams])

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return

      try {
        setLoading(true)
        
        // Fetch anime details
        const animeData = await anilistService.getAnimeById(Number(id))
        setAnime(animeData)

        // Fetch episodes
        const episodesResponse = await fetch(`/api/anime/${id}/episodes`)
        if (episodesResponse.ok) {
          const episodesData = await episodesResponse.json()
          setEpisodes(episodesData.episodes || [])
        }
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [id])

  const handleNextEpisode = () => {
    if (episodes.length > 0 && currentEpisode < episodes.length) {
      setCurrentEpisode(currentEpisode + 1)
    } else if (anime?.episodes && currentEpisode < anime.episodes) {
      setCurrentEpisode(currentEpisode + 1)
    }
  }

  const handlePreviousEpisode = () => {
    if (currentEpisode > 1) {
      setCurrentEpisode(currentEpisode - 1)
    }
  }

  const handleEpisodeSelect = (episodeNumber: number) => {
    setCurrentEpisode(episodeNumber)
    setShowEpisodeList(false)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!anime) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Anime not found</h1>
          <Link to="/" className="text-blue-400 hover:text-blue-300">
            Go back to home
          </Link>
        </div>
      </div>
    )
  }

  const totalEpisodes = episodes.length || anime.episodes || 12

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <div className="relative z-50 bg-black/80 backdrop-blur-sm border-b border-gray-800">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to={`/anime/${anime.id}`}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-6 h-6" />
              </Link>
              <div>
                <h1 className="text-lg font-semibold text-white truncate max-w-md">
                  {anime.title.english || anime.title.romaji}
                </h1>
                <p className="text-sm text-gray-400">
                  Episode {currentEpisode} of {totalEpisodes}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowEpisodeList(!showEpisodeList)}
                className="flex items-center space-x-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                <List className="w-4 h-4" />
                <span className="hidden sm:inline">Episodes</span>
              </button>
              <button className="p-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors">
                <Settings className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col lg:flex-row">
        {/* Video Player */}
        <div className="flex-1">
          <div className="aspect-video">
            <VideoPlayer
              animeId={anime.id.toString()}
              title={anime.title.english || anime.title.romaji}
              episode={currentEpisode}
              onNext={handleNextEpisode}
              onPrevious={handlePreviousEpisode}
            />
          </div>

          {/* Episode Info */}
          <div className="p-6 bg-gray-900">
            <h2 className="text-xl font-bold text-white mb-2">
              Episode {currentEpisode}
              {episodes[currentEpisode - 1]?.title && ` - ${episodes[currentEpisode - 1].title}`}
            </h2>
            
            {anime.description && (
              <p className="text-gray-300 text-sm leading-relaxed">
                {anime.description.replace(/<[^>]*>/g, '').substring(0, 300)}...
              </p>
            )}

            <div className="flex items-center space-x-4 mt-4 text-sm text-gray-400">
              <span>Duration: ~24 min</span>
              <span>•</span>
              <span>Quality: 720p</span>
              <span>•</span>
              <span>Subtitles: English</span>
            </div>
          </div>
        </div>

        {/* Episode List Sidebar */}
        {showEpisodeList && (
          <div className="w-full lg:w-80 bg-gray-900 border-l border-gray-800 max-h-screen overflow-y-auto">
            <div className="p-4 border-b border-gray-800">
              <h3 className="text-lg font-semibold text-white">Episodes</h3>
              <p className="text-sm text-gray-400">{totalEpisodes} episodes available</p>
            </div>
            
            <div className="p-2">
              {Array.from({ length: totalEpisodes }, (_, i) => i + 1).map((episodeNum) => (
                <button
                  key={episodeNum}
                  onClick={() => handleEpisodeSelect(episodeNum)}
                  className={`w-full p-3 rounded-lg mb-2 text-left transition-colors ${
                    episodeNum === currentEpisode
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">Episode {episodeNum}</div>
                      {episodes[episodeNum - 1]?.title && (
                        <div className="text-sm opacity-80 truncate">
                          {episodes[episodeNum - 1].title}
                        </div>
                      )}
                    </div>
                    {episodeNum === currentEpisode && (
                      <div className="w-2 h-2 bg-white rounded-full" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Next Episode Preview */}
      {currentEpisode < totalEpisodes && (
        <div className="bg-gray-900 border-t border-gray-800 p-6">
          <div className="container mx-auto">
            <h3 className="text-lg font-semibold text-white mb-3">Up Next</h3>
            <div className="flex items-center justify-between bg-gray-800 rounded-lg p-4">
              <div>
                <h4 className="font-medium text-white">
                  Episode {currentEpisode + 1}
                  {episodes[currentEpisode]?.title && ` - ${episodes[currentEpisode].title}`}
                </h4>
                <p className="text-sm text-gray-400 mt-1">
                  Continue watching {anime.title.english || anime.title.romaji}
                </p>
              </div>
              <button
                onClick={handleNextEpisode}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Play Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
